import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, ArrowRight, Lock } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { useDemo } from "@/components/demo/DemoUi";
import { DEMO_JOURNEY, TAG_LABELS, TAG_STYLE } from "@/config/demoJourney";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const T = {
  fr: { btn: "Parcours de démo", subtitle: "Situation → Signaux → Opportunités → Actions → Campagne → Résultats", see: "Voir cette étape", readonly: "Démo en lecture seule" },
  en: { btn: "Demo journey", subtitle: "Situation → Signals → Opportunities → Actions → Campaign → Results", see: "View this step", readonly: "Read-only demo" },
};

// Compact TopBar entry point — visible ONLY in demo context (org.is_demo).
export function DemoJourneyButton() {
  const { isDemo, readOnly } = useDemo();
  const { activeMembership } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (!isDemo) return null;
  const t = T[lang] || T.fr;
  const isViewer = activeMembership?.role === "Viewer";
  const showReadOnly = readOnly || isViewer;
  const go = (route) => { setOpen(false); navigate(route); };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="demo-journey-trigger"
          className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60"
        >
          <Compass size={13} />
          <span className="hidden sm:inline">{t.btn}</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[360px] overflow-y-auto p-0" data-testid="demo-journey-panel">
        <div className="border-b border-slate-100 p-5">
          <SheetTitle className="flex items-center gap-2 text-base font-semibold text-[#17201A]">
            <Compass size={16} className="text-indigo-600" /> {t.btn}
          </SheetTitle>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">{t.subtitle}</p>
          {showReadOnly && (
            <div data-testid="demo-journey-readonly" className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800">
              <Lock size={12} /> {t.readonly}
            </div>
          )}
        </div>
        <ol className="divide-y divide-slate-100" data-testid="demo-journey-steps">
          {DEMO_JOURNEY.map((s, i) => (
            <li key={s.id} data-testid={`demo-step-${s.id}`} className="p-5">
              <div className="flex items-start gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1E3D2B] text-[11px] font-bold text-white">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-[#17201A]">{s.title[lang] || s.title.fr}</h3>
                    <span data-testid={`demo-step-tag-${s.id}`} className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${TAG_STYLE[s.tag]}`}>
                      {(TAG_LABELS[s.tag] || {})[lang] || TAG_LABELS[s.tag].fr}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">{s.value[lang] || s.value.fr}</p>
                  <button
                    type="button"
                    data-testid={`demo-step-goto-${s.id}`}
                    onClick={() => go(s.route)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:underline"
                  >
                    {t.see} <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </SheetContent>
    </Sheet>
  );
}
